/**
 * QuickActions — 2×2 action tile grid on the user dashboard.
 *
 * Tiles are scoped to a specific trust. The trustId comes from the
 * surrounding context — the dashboard route passes it down. If no trustId
 * is available (rare: user has 0 trusts, dashboard renders an empty state
 * before this component mounts), the tiles render disabled.
 *
 * Previously this file hardcoded TRUST_ID = 'vandermerwe-family-trust'
 * and RES_ID = 'RES-2026-Q3-04' — pure demo-era fixtures. Now the
 * trustId is a prop and the sign-resolution banner uses a real
 * useResolutions() lookup for the first pending resolution.
 */

import { Link } from '@tanstack/react-router';
import { Icon } from '../icon';
import type { IconName } from '../icon';
import { useResolutions } from '../../lib/api';

interface ActionTile {
  icon: IconName;
  label: string;
  sub: string;
  badge?: string;
  primary?: boolean;
  href: string;
}

export interface QuickActionsProps {
  trustId: string | undefined;
}

export function QuickActions({ trustId }: QuickActionsProps) {
  // Resolutions awaiting THIS user's signature → the "Sign now" banner. The
  // SAME read as the dashboard headline ("N resolutions need your signature"),
  // cache-shared, narrowed to this trust: the banner once said "(1 pending)"
  // as a literal while the headline above it counted 10 (grader round 14).
  // The server filter already scopes to a real signer who hasn't signed, so a
  // member who isn't a signer is never offered a sign action. If none, the
  // banner is suppressed (don't surface fake activity).
  const { data: awaitingData } = useResolutions({ awaitingMySignature: true });
  const pendingHere = (awaitingData?.resolutions ?? []).filter(
    (r) => r.trustId === trustId,
  );
  const pendingCount = pendingHere.length;
  const pendingResolution = pendingHere[0];

  const idEnc = trustId ? encodeURIComponent(trustId) : '';
  const disabled = !trustId;
  const tiles: ActionTile[] = trustId
    ? [
        {
          // POR-02 (M1 §H): "a prominent Add assets button, pick the trust then
          // capture values." Until now the ONLY way to record an asset was to
          // already be inside a trust's Assets tab, which is where the dialog
          // is mounted — nothing on the dashboard led there, so the wealth
          // chart Paul was looking at could never be filled from the screen he
          // was looking at. First tile, and the primary one.
          icon: 'plus',
          label: 'Add assets',
          sub: 'Record property, shares or a vehicle',
          primary: true,
          href: `/app/trusts/${idEnc}/assets`,
        },
        {
          icon: 'people',
          label: 'Beneficiaries',
          sub: 'Open the register',
          href: `/app/trusts/${idEnc}/beneficiaries`,
        },
        {
          icon: 'doc',
          label: 'Documents',
          sub: 'Trust deed, LOAs, resolutions',
          href: `/app/trusts/${idEnc}/documents`,
        },
        {
          icon: 'cash',
          label: 'Distributions',
          sub: 'Payout history + drafts',
          href: `/app/trusts/${idEnc}/distributions`,
        },
        {
          icon: 'shield',
          label: 'Audit log',
          sub: 'Every state change',
          href: `/app/trusts/${idEnc}/audit`,
        },
      ]
    : [];

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="eyebrow">Take care of</div>
          <h2 className="card-title">Quick actions</h2>
        </div>
      </div>

      {disabled ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>
          Quick actions appear here once a trust is onboarded.
        </p>
      ) : (
        <div className="actions-grid">
          {tiles.map((t) => (
            <Link
              key={t.href}
              to={t.href as never}
              className={'action-tile' + (t.primary ? ' primary' : '')}
              style={{ textDecoration: 'none' }}
            >
              <div className="action-tile-icon">
                <Icon name={t.icon} size={17} />
              </div>
              <div className="action-tile-meta">
                <div className="action-tile-label">{t.label}</div>
                <div className="action-tile-sub">{t.sub}</div>
              </div>
              {t.badge && <span className="action-tile-badge">{t.badge}</span>}
            </Link>
          ))}
        </div>
      )}

      {/* Sign-resolution banner — only when there's a real pending resolution. */}
      {trustId && pendingResolution && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--hairline)' }}>
          <Link
            to={`/app/trusts/${idEnc}/resolutions/${encodeURIComponent(pendingResolution.id)}/sign` as never}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 16px',
              background: 'var(--accent-50)',
              border: '1px solid var(--accent-300)',
              borderRadius: 'var(--r-md)',
              textDecoration: 'none',
              transition: 'background 160ms var(--ease-cushion)',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: 'var(--accent-100)',
                color: 'var(--accent-700)',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name="sign" size={16} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent-800)' }}>
                {pendingCount === 1
                  ? 'Sign resolution (1 pending)'
                  : `Sign resolutions (${pendingCount} pending)`}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--accent-600)', marginTop: 1 }}>
                {pendingResolution.title ?? pendingResolution.id}
              </div>
            </div>
            <span
              style={{
                fontSize: 12.5,
                color: 'var(--accent-700)',
                fontWeight: 600,
                letterSpacing: '0.06em',
                flexShrink: 0,
              }}
            >
              Sign now →
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}
